import { Tooltip } from "react-native-paper";
import Clipboard from "@react-native-clipboard/clipboard";
import {
    Alert,
    Loading,
    DataTable,
    PageTitle,
    Surface,
    Text,
    useAuth,
    useTheme,
    Spacer,
    useToast,
} from "@venuepos/react-common";
import { AvailableLocale, useTranslation } from "locales";
import React, { View } from "react-native";
import { AdminContainer } from "../../container";
import {
    useFiskalyEstablishmentsLazyQuery,
    useFiskalyRemoteCashRegistersLazyQuery,
    useFiskalyRemoteClientsLazyQuery,
    useFiskalyRemoteEstablishmentSubmissionsLazyQuery,
    useFiskalyTssListLazyQuery,
} from "graphql-sdk";
import { RootStackScreenProps } from "../../../navigation";
import { useCallback, useEffect } from "react";
import { formatDateTime, formatRelativeDate } from "lib";
import { useAdminSession } from "../../../session";

type ScreenProps = RootStackScreenProps<"FISKALY_ORGANIZATION">;

export function FiskalyOrganizationScreen({
    route,
    navigation: { navigate },
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce(["admin.role"]);

    const id = route.params.id;

    const [{ locale }] = useAdminSession(["locale"]);
    const theme = useTheme();
    const [t] = useTranslation();
    const toast = useToast();

    const [getTssList, tssList] = useFiskalyTssListLazyQuery({
        fetchPolicy: "no-cache",
    });
    const [getClients, clients] = useFiskalyRemoteClientsLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [getEstablishments, establishments] =
        useFiskalyEstablishmentsLazyQuery({
            fetchPolicy: "no-cache",
        });

    const [getRegisters, registers] = useFiskalyRemoteCashRegistersLazyQuery({
        fetchPolicy: "no-cache",
    });

    const [getSubmissions, submissions] =
        useFiskalyRemoteEstablishmentSubmissionsLazyQuery({
            fetchPolicy: "no-cache",
        });

    const copyOnClick = useCallback(
        (text: string) => {
            Clipboard.setString(text);
            toast.info(t("backoffice.info.text_copied", "Text copied"));
        },
        [t, toast]
    );

    useEffect(() => {
        if (!id) {
            navigate("FISKALY_ORGANIZATIONS");
        } else {
            getTssList({ variables: { integrationId: id } });
            getClients({ variables: { integrationId: id } });
            getEstablishments({ variables: { integrationId: id } });
            getRegisters({ variables: { integrationId: id } });
            getSubmissions({ variables: { integrationId: id } });
        }
    }, [
        getTssList,
        navigate,
        id,
        getClients,
        getEstablishments,
        getRegisters,
        getSubmissions,
    ]);

    return (
        <AdminContainer>
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.tss_list",
                        "Fiskaly TSS"
                    )}
                </PageTitle>
                {tssList.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: tssList.error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>{`${t(
                                "backoffice.common.id",
                                "ID"
                            )} (${t(
                                "backoffice.common.click_to_copy",
                                "Click to copy"
                            )})`}</DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.status", "Status")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.description",
                                    "Description"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.created_at",
                                    "Created at"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.client_count",
                                    "No. clients"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.active_transaction_count",
                                    "No. active transactions"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.column_signature_counter",
                                    "Signature counter"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.column_transaction_counter",
                                    "Transaction counter"
                                )}
                            </DataTable.Title>
                        </DataTable.Header>
                        {tssList?.loading ? (
                            <Loading />
                        ) : (
                            tssList?.data?.fiskalyTssList?.data.map(
                                (itr, rowIndex) => (
                                    <DataTable.Row
                                        style={
                                            rowIndex % 2 === 0
                                                ? theme.styles.oddRow
                                                : null
                                        }
                                        key={`fiskaly_tss_${rowIndex}`}
                                    >
                                        <DataTable.Cell
                                            textStyle={theme.styles.link}
                                            onPress={() => copyOnClick(itr.id)}
                                        >
                                            {itr.id}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.state}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.description}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {formatRelativeDate(
                                                itr.createdAt,
                                                locale as AvailableLocale
                                            )}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.numberRegisteredClients}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.numberActiveTransactions}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.signatureCounter}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itr.transactionCounter}
                                        </DataTable.Cell>
                                    </DataTable.Row>
                                )
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
            <Spacer space={2} />
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.client_list",
                        "SIGN DE API clients"
                    )}
                </PageTitle>
                {clients?.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: clients.error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>
                                {`${t("backoffice.common.id", "ID")} (${t(
                                    "backoffice.common.click_to_copy",
                                    "Click to copy"
                                )})`}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.serial_number",
                                    "Serial number"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.status", "Status")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.tss_id",
                                    "TSS id"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.tss_status",
                                    "TSS state"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.created_at",
                                    "Created at"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.updated_at",
                                    "Updated at"
                                )}
                            </DataTable.Title>
                        </DataTable.Header>
                        {clients?.loading ? (
                            <Loading />
                        ) : (
                            clients?.data?.fiskalyRemoteClients.data.map(
                                (itr, rowIndex) => {
                                    if (!tssList.data) {
                                        return;
                                    }

                                    const foundTss =
                                        tssList.data.fiskalyTssList.data.find(
                                            tssItr => itr.tssId === tssItr.id
                                        );

                                    return (
                                        <DataTable.Row
                                            style={
                                                rowIndex % 2 === 0
                                                    ? theme.styles.oddRow
                                                    : null
                                            }
                                            key={`fiskaly_client_${rowIndex}`}
                                        >
                                            <DataTable.Cell
                                                textStyle={theme.styles.link}
                                                onPress={() =>
                                                    copyOnClick(itr.id)
                                                }
                                            >
                                                {itr.id}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.serialNumber}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.state}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.tssId}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {foundTss.state}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {formatRelativeDate(
                                                    itr.createdAt,
                                                    locale as AvailableLocale
                                                )}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.updatedAt !== 0
                                                    ? formatRelativeDate(
                                                          itr.updatedAt,
                                                          locale as AvailableLocale
                                                      )
                                                    : "-"}
                                            </DataTable.Cell>
                                        </DataTable.Row>
                                    );
                                }
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
            <Spacer space={2} />
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.cash_register_list",
                        "DSFinV-K API cash registers"
                    )}
                </PageTitle>
                {registers?.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: registers.error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.client_id",
                                    "Client ID"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.type", "Type")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.tss_id",
                                    "TSS ID"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.currency",
                                    "Currency"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.brand",
                                    "Brand"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.model",
                                    "Model"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.software_brand",
                                    "Software brand"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.software_version",
                                    "Software version"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.environment",
                                    "Environment"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.cash_register_table.revision",
                                    "Revision"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.created_at",
                                    "Created at"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.updated_at",
                                    "Updated at"
                                )}
                            </DataTable.Title>
                        </DataTable.Header>
                        {registers?.loading ? (
                            <Loading />
                        ) : registers?.data?.fiskalyRemoteCashRegisters.data
                              .length === 0 ? (
                            <View>
                                <Text>
                                    {t(
                                        "backoffice.integration.fiskaly.cash_register_table.no_rows",
                                        "No cash registers are registered yet."
                                    )}
                                </Text>
                            </View>
                        ) : (
                            registers?.data?.fiskalyRemoteCashRegisters.data.map(
                                (itm, idx) => (
                                    <DataTable.Row
                                        style={
                                            idx % 2 === 0
                                                ? theme.styles.oddRow
                                                : null
                                        }
                                        key={`fiskaly_cash_register_${idx}`}
                                    >
                                        <DataTable.Cell>
                                            <View style={theme.styles.flex1}>
                                                <Tooltip title={itm.clientId}>
                                                    <Text
                                                        numberOfLines={1}
                                                        onPress={() =>
                                                            copyOnClick(
                                                                itm.clientId
                                                            )
                                                        }
                                                        style={
                                                            theme.styles.link
                                                        }
                                                    >
                                                        {itm.clientId}
                                                    </Text>
                                                </Tooltip>
                                            </View>
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.cashRegisterType}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            <View style={theme.styles.flex1}>
                                                <Tooltip title={itm.tssId}>
                                                    <Text numberOfLines={1}>
                                                        {itm.tssId}
                                                    </Text>
                                                </Tooltip>
                                            </View>
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.baseCurrencyCode}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.brand}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.model}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.softwareBrand}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.softwareVersion}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.environment}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.revision}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.createdAt ? (
                                                <View
                                                    style={theme.styles.flex1}
                                                >
                                                    <Tooltip
                                                        title={formatDateTime(
                                                            itm.createdAt,
                                                            locale as AvailableLocale
                                                        )}
                                                    >
                                                        <Text numberOfLines={1}>
                                                            {formatRelativeDate(
                                                                itm.createdAt,
                                                                locale as AvailableLocale
                                                            )}
                                                        </Text>
                                                    </Tooltip>
                                                </View>
                                            ) : (
                                                "-"
                                            )}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.updatedAt ? (
                                                <View
                                                    style={theme.styles.flex1}
                                                >
                                                    <Tooltip
                                                        title={formatDateTime(
                                                            itm.updatedAt,
                                                            locale as AvailableLocale
                                                        )}
                                                    >
                                                        <Text numberOfLines={1}>
                                                            {formatRelativeDate(
                                                                itm.updatedAt,
                                                                locale as AvailableLocale
                                                            )}
                                                        </Text>
                                                    </Tooltip>
                                                </View>
                                            ) : (
                                                "-"
                                            )}
                                        </DataTable.Cell>
                                    </DataTable.Row>
                                )
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
            <Spacer space={2} />
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.establishment_list",
                        "SUBMIT DE API establishments"
                    )}
                </PageTitle>
                {establishments?.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: establishments.error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>
                                {t("backoffice.common.id", "ID")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.establishment_table.name",
                                    "Designation"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.street", "Street")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.house_number",
                                    "House number"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.zip", "Zip")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.town", "City")}
                            </DataTable.Title>
                        </DataTable.Header>
                        {establishments?.loading ? (
                            <Loading />
                        ) : establishments?.data?.fiskalyEstablishments.data
                              .length === 0 ? (
                            <View>
                                <Text>
                                    {t(
                                        "backoffice.integration.fiskaly.establishment_table.no_rows",
                                        "No establishments are created yet."
                                    )}
                                </Text>
                            </View>
                        ) : (
                            establishments?.data?.fiskalyEstablishments.data.map(
                                (itm, idx) => (
                                    <DataTable.Row
                                        style={
                                            idx % 2 === 0
                                                ? theme.styles.oddRow
                                                : null
                                        }
                                        key={`fiskaly_establishment_${idx}`}
                                    >
                                        <DataTable.Cell>
                                            <View style={theme.styles.flex1}>
                                                <Tooltip title={itm.id}>
                                                    <Text
                                                        numberOfLines={1}
                                                        onPress={() =>
                                                            copyOnClick(itm.id)
                                                        }
                                                        style={
                                                            theme.styles.link
                                                        }
                                                    >
                                                        {itm.id}
                                                    </Text>
                                                </Tooltip>
                                            </View>
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.designation}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.street}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.houseNumber}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.zip}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.town}
                                        </DataTable.Cell>
                                    </DataTable.Row>
                                )
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
            <Spacer space={2} />
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.submission_list",
                        "SUBMIT DE API Submissions"
                    )}
                </PageTitle>
                {submissions?.error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: submissions.error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>
                                {t("backoffice.common.id", "ID")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.establishment",
                                    "Establishment"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.error",
                                    "Error"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.state",
                                    "State"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.common.created_at",
                                    "Created at"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.transmitted_at",
                                    "Transmitted at"
                                )}
                            </DataTable.Title>
                        </DataTable.Header>
                        {submissions?.loading ? (
                            <Loading />
                        ) : submissions?.data
                              ?.fiskalyRemoteEstablishmentSubmissions.data
                              .length === 0 ? (
                            <View>
                                <Text>
                                    {t(
                                        "backoffice.integration.fiskaly.submission_table.no_rows",
                                        "No submissions yet."
                                    )}
                                </Text>
                            </View>
                        ) : (
                            submissions?.data?.fiskalyRemoteEstablishmentSubmissions.data.map(
                                (itm, idx) => (
                                    <DataTable.Row
                                        style={
                                            idx % 2 === 0
                                                ? theme.styles.oddRow
                                                : null
                                        }
                                        key={`fiskaly_submission_${idx}`}
                                    >
                                        <DataTable.Cell>
                                            <View style={theme.styles.flex1}>
                                                <Tooltip title={itm.id}>
                                                    <Text
                                                        numberOfLines={1}
                                                        onPress={() =>
                                                            copyOnClick(itm.id)
                                                        }
                                                        style={
                                                            theme.styles.link
                                                        }
                                                    >
                                                        {itm.id}
                                                    </Text>
                                                </Tooltip>
                                            </View>
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.establishmentId}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.error}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.state}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.createdAt}
                                        </DataTable.Cell>
                                        <DataTable.Cell>
                                            {itm.transmittedAt}
                                        </DataTable.Cell>
                                    </DataTable.Row>
                                )
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
        </AdminContainer>
    );
}
